import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Stethoscope, CheckCircle2, XCircle, AlertTriangle, Wrench, Loader2, MessageSquare, User } from "lucide-react";
import { V } from "@/lib/vocab";

const STATUS_ICON = {
  ok: <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />,
  warn: <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />,
  blocked: <XCircle className="h-4 w-4 text-danger-600 shrink-0 mt-0.5" />,
};

const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};

export default function SmsHealthCheck({ tenantId, defaultPhone = "" }) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [report, setReport] = useState(null);

  const openDialog = () => { setPhone(defaultPhone || ""); setReport(null); setOpen(true); };

  const run = async (ph) => {
    const target = (ph ?? phone).trim();
    if (!target) { toast.error(`Enter the ${V.lc("person")}'s phone number`); return; }
    setBusy(true);
    try {
      const r = await api.get(`/tenants/${tenantId}/sms-health`, { params: { phone: target } });
      setReport(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't run the check"); }
    finally { setBusy(false); }
  };

  const fixAll = async () => {
    if (!report?.fixable?.length) return;
    setFixing(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/sms-health/fix`, { phone: report.phone, actions: report.fixable });
      (r.data.applied || []).forEach((a) => toast.success(a));
      await run(report.phone);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't apply the fixes"); }
    finally { setFixing(false); }
  };

  const verdict = report?.verdict;

  return (
    <>
      <button data-testid="sms-health-btn" onClick={openDialog}
        className="rounded-full bg-white border border-sand-200 hover:border-ink text-ink font-semibold px-4 py-2 text-sm flex items-center gap-2 transition-colors">
        <Stethoscope className="h-4 w-4 text-redline-500" strokeWidth={2} /> Check a number
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="sms-health-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Stethoscope className="h-5 w-5 text-redline-500" /> {V.one("person")} reachability check
            </DialogTitle>
            <DialogDescription>
              Not getting replies to a number? This runs every silence-check the concierge runs and tells you exactly why — with a one-click fix.
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-2">
            <input data-testid="sms-health-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && run()}
              placeholder="(512) 555-0100"
              className="flex-1 rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
            <button data-testid="sms-health-run-btn" disabled={busy} onClick={() => run()}
              className="rounded-full bg-redline-500 text-white font-semibold px-5 py-2.5 text-sm hover:bg-redline-600 transition-colors disabled:opacity-60 flex items-center gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {busy ? "Checking…" : "Run check"}
            </button>
          </div>

          {report && (
            <div className="space-y-4">
              <div data-testid="sms-health-verdict"
                className={`rounded-xl px-4 py-3 text-sm font-semibold flex items-start gap-2 ${verdict?.will_reply ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-red-50 text-redline-700 border border-red-200"}`}>
                {verdict?.will_reply ? <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" /> : <XCircle className="h-4 w-4 shrink-0 mt-0.5" />}
                {verdict?.summary}
              </div>

              {report.fixable?.length > 0 && (
                <button data-testid="sms-health-fix-all" disabled={fixing} onClick={fixAll}
                  className="w-full rounded-xl bg-ink text-white font-semibold px-4 py-3 text-sm hover:bg-ink/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                  {fixing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />}
                  {fixing ? "Fixing…" : "Fix all issues — clear blocks & release to the AI"}
                </button>
              )}

              <div className="rounded-xl border border-sand-200 divide-y divide-sand-100 overflow-hidden">
                {report.checks.map((c) => (
                  <div key={c.id} data-testid={`sms-health-check-${c.id}`} className={`flex items-start gap-2.5 px-3.5 py-2.5 ${c.status === "blocked" ? "bg-red-50/60" : c.status === "warn" ? "bg-amber-50/50" : "bg-white"}`}>
                    {STATUS_ICON[c.status]}
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-ink">{c.label}</div>
                      <div className="text-xs text-ink-600 mt-0.5">{c.detail}</div>
                    </div>
                  </div>
                ))}
              </div>

              {report.threads?.length > 0 && (
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-1.5">Open threads ({report.threads.length})</div>
                  <div className="rounded-xl border border-sand-200 divide-y divide-sand-100 overflow-hidden">
                    {report.threads.map((t, i) => (
                      <div key={t.id} className="flex items-center gap-2 px-3.5 py-2 text-xs">
                        <User className="h-3.5 w-3.5 text-sand-400 shrink-0" />
                        <span className="font-semibold text-ink truncate">{t.active_person_name || "Unknown"}</span>
                        <span className={`px-1.5 py-0.5 rounded-full capitalize shrink-0 ${t.human_owned ? "bg-redline-50 text-redline-700" : t.status === "waiting" ? "bg-sky-50 text-sky-700" : "bg-sand-100 text-ink/60"}`}>
                          {t.human_owned ? "taken over" : (t.status || "").replace(/_/g, " ")}
                        </span>
                        {i === 0 && <span className="px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 shrink-0">texts land here</span>}
                        <span className="ml-auto text-muted-foreground shrink-0">{fmtTime(t.last_message_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {report.recent_messages?.length > 0 && (
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-1.5 flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" /> Latest traffic on the live thread
                  </div>
                  <div className="rounded-xl border border-sand-200 divide-y divide-sand-100 overflow-hidden">
                    {report.recent_messages.map((m, i) => (
                      <div key={i} className="px-3.5 py-2 text-xs flex items-start gap-2">
                        <span className={`px-1.5 py-0.5 rounded-full font-semibold shrink-0 ${m.direction === "inbound" ? "bg-sand-100 text-ink/70" : "bg-redline-50 text-redline-700"}`}>
                          {m.direction === "inbound" ? `${V.one("person")}` : m.is_human ? "Staff" : "AI"}
                        </span>
                        <span className="text-ink-600 min-w-0 break-words">{m.body || <em className="text-muted-foreground">(empty)</em>}</span>
                        <span className="ml-auto text-muted-foreground shrink-0">{fmtTime(m.created_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
